/**
 * EntityFormRoot — root form component and compound component assembly point.
 *
 * Supports two configuration modes:
 * - **Local mode**: `config` prop provides EntityFormConfig directly
 * - **Remote mode**: `remoteConfigId` prop fetches config from Origami API
 *
 * @see Spec section 3.1 for the EntityFormRoot contract.
 * @see types.ts `EntityFormProps` for the full props interface.
 */

import * as React from 'react';
import { EntityFormContext } from '../context/EntityFormContext';
import { useEntityForm } from '../hooks/useEntityForm';
import { useRemoteComponentConfig } from '../../shared';
import { Spinner, Alert } from '@components/base/feedback';
import { EntityFormHeader } from './EntityFormHeader';
import { EntityFormSection } from './EntityFormSection';
import { EntityFormField } from './EntityFormField';
import { EntityFormFields } from './EntityFormFields';
import { EntityFormActions } from './EntityFormActions';
import { EntityFormAlert } from './EntityFormAlert';
import type { EntityFormConfig, EntityFormProps } from '../types';

// ---------------------------------------------------------------------------
// Props for the inner component (config is required)
// ---------------------------------------------------------------------------

interface EntityFormInnerProps {
  config: EntityFormConfig;
  initialValues?: Record<string, unknown>;
  entityId?: string | number;
  mode?: 'create' | 'edit';
  form?: EntityFormProps['form'];
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown) => void;
  onCancel?: () => void;
  className?: string;
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// EntityFormInner — core form with guaranteed config
// ---------------------------------------------------------------------------

function EntityFormInner({
  config,
  initialValues,
  entityId,
  mode,
  form: formProp,
  onSuccess,
  onError,
  onCancel,
  className,
  children,
}: Readonly<EntityFormInnerProps>): React.ReactElement {
  const resolvedMode =
    mode ?? (entityId || initialValues ? 'edit' : 'create');

  const internalForm = useEntityForm({
    config,
    initialValues,
    entityId,
    mode: resolvedMode,
    onSuccess,
    onError,
    onCancel,
  });

  const form = formProp ?? internalForm;

  const handleSubmit = React.useCallback(
    (event: React.SyntheticEvent<HTMLFormElement>) => {
      event.preventDefault();
      void form.submit();
    },
    [form],
  );

  const contextValue = React.useMemo(
    () => ({ config, form }),
    [config, form],
  );

  const ariaLabel = config.i18n?.title ?? undefined;

  const containerClasses =
    [config.cssOverrides?.root, className].filter(Boolean).join(' ') ||
    undefined;

  return (
    <EntityFormContext.Provider value={contextValue}>
      <form
        noValidate
        onSubmit={handleSubmit}
        aria-label={ariaLabel}
        aria-busy={form.isSubmitting}
        className={containerClasses}
        data-testid="entity-form"
      >
        {children}
      </form>
    </EntityFormContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// EntityFormRemoteWrapper — fetches config, then renders inner
// ---------------------------------------------------------------------------

function EntityFormRemoteWrapper({
  remoteConfigId,
  tenant,
  module,
  loadingFallback,
  errorMessage,
  ...rest
}: Readonly<EntityFormProps>): React.ReactElement {
  const { config, isLoading, error } = useRemoteComponentConfig<EntityFormConfig>({
    componentId: remoteConfigId!,
    tenant,
    module,
    errorMessage,
  });

  if (isLoading) {
    return <>{loadingFallback ?? <Spinner />}</>;
  }

  if (error || !config) {
    return (
      <Alert variant="error">
        {error ?? 'Erreur de configuration du composant'}
      </Alert>
    );
  }

  return <EntityFormInner {...rest} config={config} />;
}

// ---------------------------------------------------------------------------
// EntityFormRoot — mode branching
// ---------------------------------------------------------------------------

function EntityFormRoot(props: Readonly<EntityFormProps>): React.ReactElement {
  const { config, remoteConfigId } = props;

  if (config && remoteConfigId) {
    throw new Error(
      'EntityForm: "config" and "remoteConfigId" are mutually exclusive. Provide one or the other, not both.',
    );
  }

  if (!config && !remoteConfigId) {
    throw new Error(
      'EntityForm: either "config" or "remoteConfigId" must be provided.',
    );
  }

  if (remoteConfigId) {
    return <EntityFormRemoteWrapper {...props} />;
  }

  // Local mode — config is guaranteed here
  return (
    <EntityFormInner
      config={config!}
      initialValues={props.initialValues}
      entityId={props.entityId}
      mode={props.mode}
      form={props.form}
      onSuccess={props.onSuccess}
      onError={props.onError}
      onCancel={props.onCancel}
      className={props.className}
    >
      {props.children}
    </EntityFormInner>
  );
}

EntityFormRoot.displayName = 'EntityFormRoot';

// ---------------------------------------------------------------------------
// Compound Component Assembly
// ---------------------------------------------------------------------------

/**
 * EntityForm compound component.
 *
 * Usage:
 * - EntityForm.Header   - Form title and description
 * - EntityForm.Section  - Field grouping with Card
 * - EntityForm.Field    - Single field renderer
 * - EntityForm.Fields   - Auto-render all fields
 * - EntityForm.Actions  - Action buttons
 * - EntityForm.Alert    - Form-level alerts
 */
const EntityForm = Object.assign(EntityFormRoot, {
  Header: EntityFormHeader,
  Section: EntityFormSection,
  Field: EntityFormField,
  Fields: EntityFormFields,
  Actions: EntityFormActions,
  Alert: EntityFormAlert,
});

export { EntityForm };
